import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Divider,
  ThemeProvider,
  CssBaseline,
  Chip,
} from "@mui/material";
import { Formik, getIn, setIn } from "formik";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getAuthToken } from "../../utils/authStorage";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../config";
import { hpcTheme, HPC_COLORS } from "./hpcTheme";
import HpcShell from "./HpcShell";
import HpcSuccess from "./HpcSuccess";
import {
  STEPS,
  GENDER_OPTIONS,
  MEMBERSHIP_TYPE_OPTIONS,
  TRAINING_GOAL_OPTIONS,
  TRAINING_TIME_OPTIONS,
  MEDICAL_CONDITIONS,
  PARQ_QUESTIONS,
  PAYMENT_METHOD_OPTIONS,
  BASE_DOCUMENT_ACK_OPTIONS,
  NIC_DOCUMENT_ACK,
  STUDENT_DOCUMENT_ACK_OPTIONS,
  MEDICAL_CLEARANCE_ACK,
  LIABILITY_TEXT,
  initialValues,
  labelFor,
  labelsForList,
  isStudentPackage,
  isPackageEligible,
  isFamilyChild,
  isFamilyPackage,
  parqRequiresMedicalClearance,
} from "./hpcFormConfig";
import {
  stepSchemas,
  validateFamilyMemberStep,
  validateFamilyMemberValues,
  validateSubmemberStep,
  validateSubmemberValues,
} from "./hpcValidation";
import MembershipPackagePicker from "./MembershipPackagePicker";
import FamilyPriceTable from "./FamilyPriceTable";
import PromotionCodeField from "./PromotionCodeField";
import {
  applyPackageToForm,
  fetchMembershipOptions,
  formatLkr,
  splitPackages,
} from "./hpcMembershipPricing";
import {
  formatRegistrationApiError,
  summarizeStepErrors,
} from "./hpcUserMessages";

const calcAgeFromDob = (dob) => {
  if (!dob) return "";
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? age : "";
};

const YesNoRow = ({ label, name, value, onChange, error }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      mb: 1.5,
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": {
        borderColor: "rgba(245, 166, 35, 0.45)",
        boxShadow: `0 0 0 1px ${HPC_COLORS.glow}`,
      },
    }}
  >
    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, lineHeight: 1.4 }}>
      {label}
    </Typography>
    <RadioGroup row name={name} value={value} onChange={onChange}>
      <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
      <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
    </RadioGroup>
    {error && <FormHelperText error>{error}</FormHelperText>}
  </Paper>
);

const SectionTitle = ({ children, subtitle }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6" sx={{ mb: subtitle ? 0.5 : 0 }}>
      {children}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

// Edit mode hides the package and payment steps; those are managed elsewhere.
const EDIT_HIDDEN_STEPS = ["package", "payment"];
const EDIT_STEPS = STEPS.filter((s) => !EDIT_HIDDEN_STEPS.includes(s.key));
// A member joining an existing group never answers payment questions.
const JOIN_STEPS = STEPS.filter((s) => s.key !== "payment");

const MEMBER_PAYMENT_KEYS = [
  "membershipType",
  "promoCode",
  "paymentMethod",
  "membershipFee",
  "registrationFee",
  "totalAmountPaid",
  "paymentDate",
  "receiptNumber",
];

// Payment proof is only confirmed by the member who pays.
const withoutPaymentProof = (documentsAck) => ({ ...documentsAck, payment_proof: false });

// Members shown on the success page: the server's ordered list for a new couple or family
// (main member first), or the one member just added to an existing group.
const successMembers = (data, values, joiningGroup) => {
  if (Array.isArray(data.members) && data.members.length > 0) return data.members;
  if (!data.group_id) return [];
  if (joiningGroup) {
    return [{
      membership_id: data.membership_id,
      full_name: values.fullName,
      role: "member",
      price_category: data.family_member_price?.price_category,
      status: data.status,
    }];
  }
  return [
    { membership_id: data.membership_id, full_name: values.fullName, role: "main", status: data.status },
    ...(data.additional_members || []).map((member) => ({ ...member, role: "member" })),
  ];
};

const toFormValues = (reg) => {
  const pick = (value, fallback = "") => (value === null || value === undefined ? fallback : value);
  return {
    ...initialValues,
    fullName: pick(reg.full_name),
    dateOfBirth: pick(reg.date_of_birth).slice(0, 10),
    age: pick(reg.age),
    gender: pick(reg.gender),
    nicPassport: pick(reg.nic_passport),
    nationality: pick(reg.nationality),
    occupation: pick(reg.occupation),
    address: pick(reg.address),
    mobileNumber: pick(reg.mobile_number),
    whatsappNumber: pick(reg.whatsapp_number),
    email: pick(reg.email),
    emergencyContactPerson: pick(reg.emergency_contact_person),
    emergencyContactRelationship: pick(reg.emergency_contact_relationship),
    emergencyContactNumber: pick(reg.emergency_contact_number),
    membershipType: pick(reg.membership_type),
    trainingGoals: reg.training_goals || [],
    preferredTrainingTimes: reg.preferred_training_times || [],
    membershipStartDate: pick(reg.membership_start_date).slice(0, 10),
    trainerPreference: pick(reg.trainer_preference),
    medicalConditions: { ...initialValues.medicalConditions, ...(reg.medical_conditions || {}) },
    takingMedication: pick(reg.taking_medication),
    medicationDetails: pick(reg.medication_details),
    hasInjuries: pick(reg.has_injuries),
    injuryDetails: pick(reg.injury_details),
    doctorRecommendations: pick(reg.doctor_recommendations),
    parqAnswers: { ...initialValues.parqAnswers, ...(reg.parq_answers || {}) },
    liabilityAgreed: Boolean(reg.liability_agreed),
    mediaConsent: pick(reg.media_consent),
    declarationName: pick(reg.declaration_name),
    declarationSignature: pick(reg.declaration_signature),
    declarationDate: pick(reg.declaration_date).slice(0, 10),
    documentsAck: {
      ...initialValues.documentsAck,
      ...(reg.documents_ack || {}),
      payment_proof: true,
    },
    additionalMember: null,
    familyMembers: [],
  };
};

const GymRegistration = () => {
  const { membershipId: editMembershipId } = useParams();
  const isEdit = Boolean(editMembershipId);
  const [editInitialValues, setEditInitialValues] = useState(null);
  const [editLoadError, setEditLoadError] = useState(null);
  const [activeStep, setActiveStep] = useState(isEdit ? 1 : 0);
  const [activeMember, setActiveMember] = useState("primary");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [membershipPackages, setMembershipPackages] = useState([]);
  const [membershipOptionsLoading, setMembershipOptionsLoading] = useState(false);
  const [membershipOptionsError, setMembershipOptionsError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const groupId = new URLSearchParams(location.search).get("groupId");
  const [groupInfo, setGroupInfo] = useState(null);
  const [groupError, setGroupError] = useState(null);
  // Latest server family quote, used to restore fees when a promotion is removed.
  const lastFamilyQuoteRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;
    const token = getAuthToken();
    axios
      .get(`${config.apiBaseUrl}/hpc/admin/registrations/${editMembershipId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((response) => {
        if (!cancelled) setEditInitialValues(toFormValues(response.data.registration || response.data));
      })
      .catch(() => {
        if (!cancelled) setEditLoadError("Could not load this member. They may have been deleted.");
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, editMembershipId]);

  useEffect(() => {
    if (!groupId) {
      setGroupInfo(null);
      setGroupError(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${config.apiBaseUrl}/hpc/gym-registration/group/${groupId}`)
      .then((response) => {
        if (!cancelled) setGroupInfo(response.data);
      })
      .catch(() => {
        if (!cancelled) {
          setGroupError("Could not load this group. You can still register as a new member.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const loadMembershipOptions = useCallback(async () => {
    setMembershipOptionsLoading(true);
    setMembershipOptionsError(null);
    try {
      const data = await fetchMembershipOptions();
      setMembershipPackages(data.packages || []);
    } catch {
      setMembershipOptionsError("Could not load membership options. Please try again.");
    } finally {
      setMembershipOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeStep === 0) {
      loadMembershipOptions();
    }
  }, [activeStep, loadMembershipOptions]);

  // Joining members skip payment, so payment proof is not asked of them.
  const skipPaymentStep = isEdit || Boolean(groupInfo);

  const validateStep = async (values, step) => {
    const target = groupInfo
      ? { ...values, documentsAck: { ...values.documentsAck, payment_proof: true } }
      : values;
    try {
      await stepSchemas[step].validate(target, { abortEarly: false });
      return {};
    } catch (err) {
      const errors = {};
      if (err.inner) {
        err.inner.forEach((e) => {
          if (e.path) errors[e.path] = e.message;
        });
      }
      return errors;
    }
  };

  const buildPayload = (values) => ({
    fullName: values.fullName,
    dateOfBirth: values.dateOfBirth,
    age: Number(values.age),
    gender: values.gender,
    nicPassport: values.nicPassport?.trim() || null,
    nationality: values.nationality,
    occupation: values.occupation,
    address: values.address,
    mobileNumber: values.mobileNumber,
    whatsappNumber: values.whatsappNumber,
    email: values.email,
    emergencyContactPerson: values.emergencyContactPerson,
    emergencyContactRelationship: values.emergencyContactRelationship,
    emergencyContactNumber: values.emergencyContactNumber,
    membershipType: values.membershipType,
    trainingGoals: values.trainingGoals,
    preferredTrainingTimes: values.preferredTrainingTimes,
    membershipStartDate: values.membershipStartDate,
    trainerPreference: values.trainerPreference || null,
    medicalConditions: values.medicalConditions,
    takingMedication: values.takingMedication,
    medicationDetails: values.medicationDetails,
    hasInjuries: values.hasInjuries,
    injuryDetails: values.injuryDetails,
    doctorRecommendations: values.doctorRecommendations,
    parqAnswers: values.parqAnswers,
    liabilityAgreed: values.liabilityAgreed,
    mediaConsent: values.mediaConsent,
    paymentMethod: values.paymentMethod,
    membershipFee: Number(values.membershipFee),
    registrationFee: Number(values.registrationFee),
    totalAmountPaid: Number(values.totalAmountPaid),
    paymentDate: values.paymentDate,
    receiptNumber: values.receiptNumber,
    declarationName: values.declarationName,
    declarationSignature: values.declarationSignature,
    declarationDate: values.declarationDate,
    documentsAck: values.documentsAck,
    promoCode: values.promoCode || undefined,
  });

  // Back to the package's normal fees (no promotion); family fees come from the last quote.
  const restoreBaseFees = (values, setFieldValue) => {
    const familyQuote = lastFamilyQuoteRef.current;
    if (isFamilyPackage(values.membershipType)) {
      if (familyQuote) {
        setFieldValue("membershipFee", String(familyQuote.membership_total));
        setFieldValue("registrationFee", String(familyQuote.admission_total));
        setFieldValue("totalAmountPaid", String(familyQuote.total));
      }
      return;
    }
    const pkg = membershipPackages.find((item) => item.id === values.membershipType);
    if (pkg) applyPackageToForm(pkg, setFieldValue);
  };

  // Additional and joining members send no payment details; the main member pays.
  const buildAdditionalMemberPayload = (values) => {
    const payload = buildPayload(values);
    MEMBER_PAYMENT_KEYS.forEach((key) => delete payload[key]);
    payload.documentsAck = withoutPaymentProof(values.documentsAck);
    return payload;
  };

  const handleSubmit = async (values, formikHelpers) => {
    if (isEdit) {
      setSubmitting(true);
      try {
        const payload = buildPayload(values);
        [
          "membershipType",
          "paymentMethod",
          "membershipFee",
          "registrationFee",
          "totalAmountPaid",
          "paymentDate",
          "receiptNumber",
        ].forEach((key) => delete payload[key]);
        const token = getAuthToken();
        await axios.put(
          `${config.apiBaseUrl}/hpc/admin/registrations/${editMembershipId}`,
          { ...payload, membershipType: values.membershipType },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        toast.success("Member updated");
        navigate(`/hpc/members/${editMembershipId}`);
      } catch (err) {
        toast.error(formatRegistrationApiError(err.response?.data));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setSubmitting(true);
    try {
      const url = groupInfo
        ? `${config.apiBaseUrl}/hpc/gym-registration/group/${groupId}/members`
        : `${config.apiBaseUrl}/hpc/gym-registration`;
      const payload = groupInfo ? buildAdditionalMemberPayload(values) : buildPayload(values);
      const selectedPackage = membershipPackages.find((pkg) => pkg.id === values.membershipType);
      if (
        !groupInfo &&
        selectedPackage?.category === "couple" &&
        values.additionalMember
      ) {
        payload.additionalMembers = [
          buildAdditionalMemberPayload(values.additionalMember),
        ];
      }
      if (
        !groupInfo &&
        selectedPackage?.category === "family" &&
        (values.familyMembers || []).length > 0
      ) {
        payload.additionalMembers = values.familyMembers.map(buildAdditionalMemberPayload);
      }
      const response = await axios.post(url, payload);
      const data = response.data;
      setSuccess({
        promotion: data.promotion || null,
        membershipId: data.membership_id,
        fullName: values.fullName,
        message: data.message,
        status: data.status,
        groupId: data.group_id,
        maxMembers: data.max_members,
        currentMembers: data.current_members,
        members: successMembers(data, values, Boolean(groupInfo)),
        registrationDate: new Date().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        isFamily: isFamilyPackage(values.membershipType),
        familyTotal: data.family_quote?.total ?? data.family_member_price?.total,
      });
    } catch (err) {
      const promoError = err.response?.data?.errors?.promoCode;
      if (promoError && formikHelpers) {
        // The code can no longer be used: remove it and show the normal price again.
        formikHelpers.setFieldValue("promoCode", "");
        formikHelpers.setFieldValue("promoPricing", null);
        restoreBaseFees(values, formikHelpers.setFieldValue);
        toast.error(`${promoError} Your promotion code was removed.`);
      } else {
        toast.error(formatRegistrationApiError(err.response?.data));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const copyMembershipId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success("Membership ID copied");
    } catch {
      toast.info(id);
    }
  };

  if (success) {
    return (
      <HpcSuccess
        membershipId={success.membershipId}
        message={success.message}
        status={success.status}
        groupId={success.groupId}
        maxMembers={success.maxMembers}
        currentMembers={success.currentMembers}
        members={success.members}
        fullName={success.fullName}
        registrationDate={success.registrationDate}
        onCopy={() => copyMembershipId(success.membershipId)}

        onAddMember={() => navigate(`/hpc/register?groupId=${success.groupId}`)}
        isFamily={success.isFamily}
        familyTotal={success.familyTotal}
      />
    );
  }

  const stepFooter = (onBack, onPrimary, submitting, isLast) => (
    <>
      <Button
        variant="outlined"
        disabled={activeStep === (isEdit ? 1 : 0) || submitting}
        onClick={onBack}
        sx={{ flex: 1, minHeight: 48 }}
      >
        Back
      </Button>
      <Button
        variant="contained"
        onClick={onPrimary}
        disabled={submitting}
        sx={{ flex: 2, minHeight: 48 }}
      >
        {isLast
          ? isEdit
            ? submitting
              ? "Saving…"
              : "Save changes"
            : submitting
              ? "Submitting…"
              : "Complete registration"
          : "Continue"}
      </Button>
    </>
  );

  if (isEdit && !editInitialValues) {
    return (
      <ThemeProvider theme={hpcTheme}>
        <CssBaseline />
        <Box sx={{ p: 4 }}>
          {editLoadError ? (
            <Alert severity="error">{editLoadError}</Alert>
          ) : (
            <Typography>Loading member…</Typography>
          )}
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={hpcTheme}>
      <CssBaseline />
      <Formik
          initialValues={editInitialValues || {
            ...initialValues,
            medicalConditions: { ...initialValues.medicalConditions },
            parqAnswers: { ...initialValues.parqAnswers },
            documentsAck: { ...initialValues.documentsAck },
            additionalMember: null,
            familyMembers: [],
            promoCode: "",
            promoPricing: null,
          }}
          onSubmit={handleSubmit}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
            setErrors,
            handleSubmit: formikSubmit,
          }) => {
            const toggleInArray = (field, item) => {
              const arr = values[field] || [];
              if (arr.includes(item)) {
                setFieldValue(
                  field,
                  arr.filter((x) => x !== item),
                );
              } else {
                setFieldValue(field, [...arr, item]);
              }
            };

            const {
              foundingAdult,
              foundingStudent,
              commitmentAdult,
              commitmentStudent,
              coupleAdult,
              family,
            } = splitPackages(membershipPackages);
            const selectedPackage = membershipPackages.find((p) => p.id === values.membershipType);
            const memberAge = values.age === "" ? "" : Number(values.age);
            const medicalClearanceRequired = parqRequiresMedicalClearance(values.parqAnswers);
            const studentMember = isStudentPackage(values.membershipType);
            const familySelected = isFamilyPackage(values.membershipType);
            // Couple and Family: only the main member answers payment questions.
            const groupPackage =
              !isEdit && !groupId && (selectedPackage?.category === "couple" || familySelected);
            // A new family registers everyone at once; joining an existing family adds one member.
            const initialFamily = !isEdit && !groupId && familySelected;
            const familyQuoteMembers = initialFamily
              ? [values, ...(values.familyMembers || [])]
              : [];
            // Fees shown on the payment step come from the server's family quote.
            const handleFamilyQuote = (quote) => {
              lastFamilyQuoteRef.current = quote;
              // With a promotion applied, the promotion quote sets the fees instead.
              if (values.promoCode) return;
              setFieldValue("membershipFee", String(quote.membership_total));
              setFieldValue("registrationFee", String(quote.admission_total));
              setFieldValue("totalAmountPaid", String(quote.total));
            };

            // Promotion codes: main member only, never for founding packages or joining members.
            const promotionAllowed =
              !isEdit && !groupInfo && Boolean(selectedPackage) && selectedPackage.category !== "founding";
            // Members only matter for Family prices; other packages are priced by package alone.
            const promotionMembers = initialFamily ? familyQuoteMembers : [];
            const handlePromotionApplied = (code, pricing) => {
              setFieldValue("promoCode", code);
              setFieldValue("promoPricing", pricing);
              setFieldValue("membershipFee", String(pricing.final_membership_fee));
              setFieldValue("registrationFee", String(pricing.final_admission_fee));
              setFieldValue("totalAmountPaid", String(pricing.final_total));
            };
            const handlePromotionRemoved = (message) => {
              setFieldValue("promoCode", "");
              setFieldValue("promoPricing", null);
              restoreBaseFees(values, setFieldValue);
              if (message) toast.info(message);
            };

            const handleSelectPackage = (pkg) => {
              if (groupInfo && pkg.id !== groupInfo.membership_type) {
                return;
              }
              if (memberAge !== "" && !isPackageEligible(pkg.id, memberAge)) {
                return;
              }
              applyPackageToForm(pkg, setFieldValue);
              if (pkg.category !== "couple") {
                setFieldValue("additionalMember", null);
              }
              if (pkg.category !== "family") {
                setFieldValue("familyMembers", []);
              }
              setFieldTouched("membershipType", true);
            };

            // Auto-select the group's package once options and group info are both loaded.
            if (
              groupInfo &&
              !values.membershipType &&
              membershipPackages.length > 0
            ) {
              const groupPkg = membershipPackages.find((p) => p.id === groupInfo.membership_type);
              if (groupPkg) {
                applyPackageToForm(groupPkg, setFieldValue);
                setFieldTouched("membershipType", true);
              }
            }

            // The same section renderers are used for the primary and the second member;
            // each member passes in its own values, errors, touched state and setters.
            const makeSectionRenderers = ({
              values,
              errors,
              touched,
              setFieldValue,
              toggleInArray,
              medicalClearanceRequired,
              studentMember,
              nicOptional = studentMember,
              showPaymentProof = true,
            }) => {
            const renderPersonal = () => (
              <>
                <SectionTitle>Personal information</SectionTitle>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Full name"
                  name="fullName"
                  value={values.fullName}
                  onChange={(e) => setFieldValue("fullName", e.target.value)}
                  error={touched.fullName && Boolean(errors.fullName)}
                  helperText={touched.fullName && errors.fullName}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Date of birth"
                  name="dateOfBirth"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={values.dateOfBirth}
                  onChange={(e) => {
                    setFieldValue("dateOfBirth", e.target.value);
                    const age = calcAgeFromDob(e.target.value);
                    if (age !== "") setFieldValue("age", age);
                  }}
                  error={touched.dateOfBirth && Boolean(errors.dateOfBirth)}
                  helperText={touched.dateOfBirth && errors.dateOfBirth}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Age"
                  name="age"
                  type="number"
                  value={values.age}
                  onChange={(e) => setFieldValue("age", e.target.value)}
                  error={touched.age && Boolean(errors.age)}
                  helperText={touched.age && errors.age}
                />
                <FormControl component="fieldset" margin="normal" error={touched.gender && Boolean(errors.gender)}>
                  <FormLabel>Gender</FormLabel>
                  <RadioGroup
                    value={values.gender}
                    onChange={(e) => setFieldValue("gender", e.target.value)}
                  >
                    {GENDER_OPTIONS.map((o) => (
                      <FormControlLabel key={o.value} value={o.value} control={<Radio />} label={o.label} />
                    ))}
                  </RadioGroup>
                  {touched.gender && errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
                </FormControl>
                <TextField
                  fullWidth
                  margin="normal"
                  label="NIC / Passport no (optional)"
                  value={values.nicPassport}
                  onChange={(e) => setFieldValue("nicPassport", e.target.value)}
                  error={touched.nicPassport && Boolean(errors.nicPassport)}
                  helperText={touched.nicPassport && errors.nicPassport}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Nationality"
                  value={values.nationality}
                  onChange={(e) => setFieldValue("nationality", e.target.value)}
                  error={touched.nationality && Boolean(errors.nationality)}
                  helperText={touched.nationality && errors.nationality}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Occupation"
                  value={values.occupation}
                  onChange={(e) => setFieldValue("occupation", e.target.value)}
                  error={touched.occupation && Boolean(errors.occupation)}
                  helperText={touched.occupation && errors.occupation}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Address"
                  multiline
                  minRows={2}
                  value={values.address}
                  onChange={(e) => setFieldValue("address", e.target.value)}
                  error={touched.address && Boolean(errors.address)}
                  helperText={touched.address && errors.address}
                />
              </>
            );

            const renderContact = () => (
              <>
                <SectionTitle>Contact & emergency</SectionTitle>
                {[
                  ["mobileNumber", "Mobile number", "tel"],
                  ["whatsappNumber", "WhatsApp number", "tel"],
                  ["email", "Email address", "email"],
                  ["emergencyContactPerson", "Emergency contact person", "text"],
                  ["emergencyContactRelationship", "Relationship", "text"],
                  ["emergencyContactNumber", "Emergency contact number", "tel"],
                ].map(([field, label, type]) => (
                  <TextField
                    key={field}
                    fullWidth
                    margin="normal"
                    label={label}
                    type={type}
                    value={values[field]}
                    onChange={(e) => setFieldValue(field, e.target.value)}
                    error={touched[field] && Boolean(errors[field])}
                    helperText={touched[field] && errors[field]}
                  />
                ))}
              </>
            );

            const renderPackageSelection = () => (
              <>
                <SectionTitle subtitle="Choose your membership package before completing your registration details.">
                  Select your package
                </SectionTitle>
                {groupInfo && isFamilyPackage(groupInfo.membership_type) && (
                  <Alert severity={groupInfo.active ? "info" : "warning"} sx={{ mb: 2 }}>
                    {groupInfo.active
                      ? `Adding a member to family group ${groupInfo.group_id}. The price is set at the next position in the family, and this member stays pending until their payment is recorded.`
                      : `Family group ${groupInfo.group_id} isn't active yet. The main member must be activated before more members can be added.`}
                  </Alert>
                )}
                {groupInfo && !isFamilyPackage(groupInfo.membership_type) && (
                  <Alert severity={groupInfo.active ? "info" : "warning"} sx={{ mb: 2 }}>
                    {groupInfo.active
                      ? `Adding member ${groupInfo.current_members + 1} of ${groupInfo.max_members} to group ${groupInfo.group_id}. Package is locked to ${groupInfo.membership_type}.`
                      : `Group ${groupInfo.group_id} isn't active yet. The primary member must be activated before more members can be added.`}
                  </Alert>
                )}
                {groupError && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {groupError}
                  </Alert>
                )}
                <MembershipPackagePicker
                  foundingAdult={foundingAdult}
                  foundingStudent={foundingStudent}
                  commitmentAdult={commitmentAdult}
                  commitmentStudent={commitmentStudent}
                  coupleAdult={coupleAdult}
                  family={family}
                  selectedId={values.membershipType}
                  onSelect={handleSelectPackage}
                  loading={membershipOptionsLoading}
                  error={membershipOptionsError}
                  onRetry={loadMembershipOptions}
                  memberAge={memberAge}
                />
                {touched.membershipType && errors.membershipType && (
                  <FormHelperText error sx={{ mb: 2 }}>
                    {errors.membershipType}
                  </FormHelperText>
                )}
                {selectedPackage && (
                  <Box sx={{ width: "100%", mb: 2, display: "block" }}>
                    <Chip
                      label={
                        selectedPackage.category === "family"
                          ? `Selected: ${selectedPackage.label} — priced per member; the family total is shown before you submit`
                          : `Selected: ${selectedPackage.label} — ${formatLkr(selectedPackage.totalFee)}`
                      }
                      color="primary"
                      sx={{
                        maxWidth: "100%",
                        height: "auto",
                        "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
                      }}
                    />
                  </Box>
                )}
              </>
            );

            const renderMembershipDetails = () => (
              <>
                <SectionTitle subtitle="Tell us about your training preferences.">
                  Membership details
                </SectionTitle>
                <FormControl
                  component="fieldset"
                  margin="normal"
                  fullWidth
                  error={touched.trainingGoals && Boolean(errors.trainingGoals)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    minWidth: 0,
                    border: 0,
                    p: 0,
                    m: 0,
                    mt: 2,
                  }}
                >
                  <FormLabel component="legend" sx={{ px: 0, mb: 1 }}>
                    Training goals
                  </FormLabel>
                  {TRAINING_GOAL_OPTIONS.map((o) => (
                    <FormControlLabel
                      key={o.value}
                      sx={{ ml: 0, width: "100%" }}
                      control={
                        <Checkbox
                          checked={values.trainingGoals.includes(o.value)}
                          onChange={() => toggleInArray("trainingGoals", o.value)}
                        />
                      }
                      label={o.label}
                    />
                  ))}
                  {touched.trainingGoals && errors.trainingGoals && (
                    <FormHelperText>{errors.trainingGoals}</FormHelperText>
                  )}
                </FormControl>
                <FormControl
                  component="fieldset"
                  margin="normal"
                  fullWidth
                  error={touched.preferredTrainingTimes && Boolean(errors.preferredTrainingTimes)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    minWidth: 0,
                    border: 0,
                    p: 0,
                    m: 0,
                    mt: 2,
                  }}
                >
                  <FormLabel component="legend" sx={{ px: 0, mb: 1 }}>
                    Preferred training time
                  </FormLabel>
                  {TRAINING_TIME_OPTIONS.map((o) => (
                    <FormControlLabel
                      key={o.value}
                      sx={{ ml: 0, width: "100%" }}
                      control={
                        <Checkbox
                          checked={values.preferredTrainingTimes.includes(o.value)}
                          onChange={() => toggleInArray("preferredTrainingTimes", o.value)}
                        />
                      }
                      label={o.label}
                    />
                  ))}
                  {touched.preferredTrainingTimes && errors.preferredTrainingTimes && (
                    <FormHelperText>{errors.preferredTrainingTimes}</FormHelperText>
                  )}
                </FormControl>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Membership start date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={values.membershipStartDate}
                  onChange={(e) => setFieldValue("membershipStartDate", e.target.value)}
                  error={touched.membershipStartDate && Boolean(errors.membershipStartDate)}
                  helperText={touched.membershipStartDate && errors.membershipStartDate}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Trainer preference (optional)"
                  value={values.trainerPreference}
                  onChange={(e) => setFieldValue("trainerPreference", e.target.value)}
                />
              </>
            );

            const renderHealth = () => (
              <>
                <SectionTitle
                  subtitle="Please answer honestly for your own safety."
                >
                  Health & medical
                </SectionTitle>
                {MEDICAL_CONDITIONS.map(({ key, label }) => (
                  <YesNoRow
                    key={key}
                    label={label}
                    name={`medicalConditions.${key}`}
                    value={values.medicalConditions[key]}
                    onChange={(e) => setFieldValue(`medicalConditions.${key}`, e.target.value)}
                    error={
                      touched.medicalConditions?.[key] &&
                      errors.medicalConditions?.[key]
                    }
                  />
                ))}
                <YesNoRow
                  label="Are you currently taking medication?"
                  value={values.takingMedication}
                  onChange={(e) => setFieldValue("takingMedication", e.target.value)}
                  error={touched.takingMedication && errors.takingMedication}
                />
                {values.takingMedication === "yes" && (
                  <TextField
                    fullWidth
                    margin="normal"
                    label="If yes, please specify"
                    multiline
                    value={values.medicationDetails}
                    onChange={(e) => setFieldValue("medicationDetails", e.target.value)}
                    error={touched.medicationDetails && Boolean(errors.medicationDetails)}
                    helperText={touched.medicationDetails && errors.medicationDetails}
                  />
                )}
                <YesNoRow
                  label="Do you have any injuries or physical limitations?"
                  value={values.hasInjuries}
                  onChange={(e) => setFieldValue("hasInjuries", e.target.value)}
                  error={touched.hasInjuries && errors.hasInjuries}
                />
                {values.hasInjuries === "yes" && (
                  <TextField
                    fullWidth
                    margin="normal"
                    label="If yes, please explain"
                    multiline
                    value={values.injuryDetails}
                    onChange={(e) => setFieldValue("injuryDetails", e.target.value)}
                    error={touched.injuryDetails && Boolean(errors.injuryDetails)}
                    helperText={touched.injuryDetails && errors.injuryDetails}
                  />
                )}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Doctor's recommendations / restrictions"
                  multiline
                  value={values.doctorRecommendations}
                  onChange={(e) => setFieldValue("doctorRecommendations", e.target.value)}
                />
              </>
            );

            const renderParq = () => (
              <>
                <SectionTitle subtitle="Please answer YES or NO to each question.">
                  PAR-Q
                </SectionTitle>
                {PARQ_QUESTIONS.map(({ key, label }) => (
                  <YesNoRow
                    key={key}
                    label={label}
                    value={values.parqAnswers[key]}
                    onChange={(e) => setFieldValue(`parqAnswers.${key}`, e.target.value)}
                    error={touched.parqAnswers?.[key] && errors.parqAnswers?.[key]}
                  />
                ))}
                {medicalClearanceRequired && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    At least one PAR-Q answer requires medical clearance before you can start
                    training.
                  </Alert>
                )}
              </>
            );

            const renderAgreement = () => (
              <>
                <SectionTitle>Liability waiver & agreement</SectionTitle>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    maxHeight: 220,
                    overflow: "auto",
                    borderLeft: `4px solid ${HPC_COLORS.hpuOrange}`,
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.6 }}>
                    {LIABILITY_TEXT}
                  </Typography>
                </Paper>
                <FormControl
                  error={touched.liabilityAgreed && Boolean(errors.liabilityAgreed)}
                  sx={{ mt: 2, mb: 1, display: "block" }}
                >
                  <FormControlLabel
                    sx={{ alignItems: "flex-start", mr: 0 }}
                    control={
                      <Checkbox
                        checked={values.liabilityAgreed}
                        onChange={(e) => setFieldValue("liabilityAgreed", e.target.checked)}
                        sx={{ pt: 0.5 }}
                      />
                    }
                    label="I have read and understood the above terms and conditions."
                  />
                  {touched.liabilityAgreed && errors.liabilityAgreed && (
                    <FormHelperText sx={{ ml: 4 }}>{errors.liabilityAgreed}</FormHelperText>
                  )}
                </FormControl>
              </>
            );

            const renderPayment = () => (
              <>
                <SectionTitle>Payment information</SectionTitle>
                <FormControl component="fieldset" margin="normal" error={touched.paymentMethod && Boolean(errors.paymentMethod)}>
                  <FormLabel>Payment method</FormLabel>
                  <RadioGroup
                    value={values.paymentMethod}
                    onChange={(e) => setFieldValue("paymentMethod", e.target.value)}
                  >
                    {PAYMENT_METHOD_OPTIONS.map((o) => (
                      <FormControlLabel key={o.value} value={o.value} control={<Radio />} label={o.label} />
                    ))}
                  </RadioGroup>
                  {touched.paymentMethod && errors.paymentMethod && (
                    <FormHelperText>{errors.paymentMethod}</FormHelperText>
                  )}
                </FormControl>
                {initialFamily && (
                  <FamilyPriceTable
                    membershipType={values.membershipType}
                    members={familyQuoteMembers}
                    onQuote={handleFamilyQuote}
                  />
                )}
                {promotionAllowed && (
                  <PromotionCodeField
                    membershipType={values.membershipType}
                    members={promotionMembers}
                    nicPassport={values.nicPassport}
                    appliedCode={values.promoCode}
                    appliedPricing={values.promoPricing}
                    onApplied={handlePromotionApplied}
                    onRemoved={handlePromotionRemoved}
                  />
                )}
                {groupPackage && (
                  <Alert severity="info" sx={{ my: 2 }}>
                    {familySelected
                      ? "This payment covers the whole family. Other family members are not asked for payment details."
                      : "This payment covers both Couple members. The second member is not asked for payment details."}
                  </Alert>
                )}
                {[
                  ["membershipFee", "Membership fee"],
                  ["registrationFee", "Registration fee"],
                  ["totalAmountPaid", "Total amount paid"],
                ].map(([field, label]) => (
                  <TextField
                    key={field}
                    fullWidth
                    margin="normal"
                    label={label}
                    type="number"
                    inputProps={{ min: 0, step: "0.01", readOnly: true }}
                    value={values[field]}
                    InputProps={{ readOnly: true }}
                    error={touched[field] && Boolean(errors[field])}
                    helperText={
                      (touched[field] && errors[field]) ||
                      (field === "membershipFee"
                        ? familySelected
                          ? "Calculated by the gym for your family."
                          : "Set from your selected membership package."
                        : undefined)
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "rgba(0,0,0,0.35)",
                      },
                    }}
                  />
                ))}
                {!values.membershipType && (
                  <FormHelperText error sx={{ mt: -1, mb: 1 }}>
                    Go back to Package selection and select a package.
                  </FormHelperText>
                )}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Payment date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={values.paymentDate}
                  onChange={(e) => setFieldValue("paymentDate", e.target.value)}
                  error={touched.paymentDate && Boolean(errors.paymentDate)}
                  helperText={touched.paymentDate && errors.paymentDate}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Receipt number (optional)"
                  value={values.receiptNumber}
                  onChange={(e) => setFieldValue("receiptNumber", e.target.value)}
                />
              </>
            );

            const renderFinish = () => (
              <>
                <SectionTitle>Media consent</SectionTitle>
                <FormControl component="fieldset" margin="normal" error={touched.mediaConsent && Boolean(errors.mediaConsent)}>
                  <RadioGroup
                    value={values.mediaConsent}
                    onChange={(e) => setFieldValue("mediaConsent", e.target.value)}
                  >
                    <FormControlLabel
                      value="allow"
                      control={<Radio />}
                      label="I agree to allow the gym to use photographs/videos for promotional purposes."
                    />
                    <FormControlLabel
                      value="deny"
                      control={<Radio />}
                      label="I do NOT wish my photos/videos to be used."
                    />
                  </RadioGroup>
                  {touched.mediaConsent && errors.mediaConsent && (
                    <FormHelperText>{errors.mediaConsent}</FormHelperText>
                  )}
                </FormControl>
                <Divider sx={{ my: 2 }} />
                <SectionTitle>Member declaration</SectionTitle>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  I confirm that the information provided is true and accurate.
                </Typography>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Member name"
                  value={values.declarationName}
                  onChange={(e) => setFieldValue("declarationName", e.target.value)}
                  error={touched.declarationName && Boolean(errors.declarationName)}
                  helperText={touched.declarationName && errors.declarationName}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Member signature (type your full name)"
                  value={values.declarationSignature}
                  onChange={(e) => setFieldValue("declarationSignature", e.target.value)}
                  error={touched.declarationSignature && Boolean(errors.declarationSignature)}
                  helperText={touched.declarationSignature && errors.declarationSignature}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={values.declarationDate}
                  onChange={(e) => setFieldValue("declarationDate", e.target.value)}
                  error={touched.declarationDate && Boolean(errors.declarationDate)}
                  helperText={touched.declarationDate && errors.declarationDate}
                />
                <Divider sx={{ my: 2 }} />
                <SectionTitle>Required documents</SectionTitle>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Confirm you will provide the following:
                </Typography>
                {medicalClearanceRequired && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Medical clearance is required before you can start training based on your PAR-Q
                    answers.
                  </Alert>
                )}
                <Box sx={{ pb: 1 }}>
                  {BASE_DOCUMENT_ACK_OPTIONS.filter(
                    ({ key }) => showPaymentProof || key !== "payment_proof",
                  ).map(({ key, label }) => (
                    <FormControlLabel
                      key={key}
                      sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
                      control={
                        <Checkbox
                          checked={values.documentsAck[key]}
                          onChange={(e) => setFieldValue(`documentsAck.${key}`, e.target.checked)}
                          sx={{ pt: 0.25 }}
                        />
                      }
                      label={label}
                    />
                  ))}
                  <FormControlLabel
                    sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
                    control={
                      <Checkbox
                        checked={values.documentsAck[NIC_DOCUMENT_ACK.key]}
                        onChange={(e) =>
                          setFieldValue(`documentsAck.${NIC_DOCUMENT_ACK.key}`, e.target.checked)
                        }
                        sx={{ pt: 0.25 }}
                      />
                    }
                    label={
                      nicOptional
                        ? `${NIC_DOCUMENT_ACK.label} (optional)`
                        : NIC_DOCUMENT_ACK.label
                    }
                  />
                </Box>
                {studentMember && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                      Student documents
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      At least one of the following is required.
                    </Typography>
                    <Box sx={{ pb: 1 }}>
                      {STUDENT_DOCUMENT_ACK_OPTIONS.map(({ key, label }) => (
                        <FormControlLabel
                          key={key}
                          sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
                          control={
                            <Checkbox
                              checked={values.documentsAck[key]}
                              onChange={(e) =>
                                setFieldValue(`documentsAck.${key}`, e.target.checked)
                              }
                              sx={{ pt: 0.25 }}
                            />
                          }
                          label={label}
                        />
                      ))}
                    </Box>
                  </>
                )}
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  Medical clearance
                </Typography>
                <Box sx={{ pb: 1 }}>
                  <FormControlLabel
                    sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
                    control={
                      <Checkbox
                        checked={values.documentsAck[MEDICAL_CLEARANCE_ACK.key]}
                        onChange={(e) =>
                          setFieldValue(
                            `documentsAck.${MEDICAL_CLEARANCE_ACK.key}`,
                            e.target.checked,
                          )
                        }
                        sx={{ pt: 0.25 }}
                      />
                    }
                    label={
                      medicalClearanceRequired
                        ? MEDICAL_CLEARANCE_ACK.labelRequired
                        : MEDICAL_CLEARANCE_ACK.labelOptional
                    }
                  />
                </Box>
                {touched.documentsAck && typeof errors.documentsAck === "string" && (
                  <FormHelperText error>{errors.documentsAck}</FormHelperText>
                )}
                {touched.documentsAck &&
                  errors.documentsAck &&
                  typeof errors.documentsAck === "object" &&
                  Object.entries(errors.documentsAck).map(([key, msg]) => (
                    <FormHelperText key={key} error>
                      {msg}
                    </FormHelperText>
                  ))}
              </>
            );

            return {
              renderPersonal,
              renderContact,
              renderPackageSelection,
              renderMembershipDetails,
              renderHealth,
              renderParq,
              renderAgreement,
              renderPayment,
              renderFinish,
            };
            };

            const {
              renderPersonal,
              renderContact,
              renderPackageSelection,
              renderMembershipDetails,
              renderHealth,
              renderParq,
              renderAgreement,
              renderPayment,
              renderFinish,
            } = makeSectionRenderers({
              values,
              errors,
              touched,
              setFieldValue,
              toggleInArray,
              medicalClearanceRequired,
              studentMember,
              nicOptional: studentMember || isFamilyChild(values.membershipType, values.age),
              showPaymentProof: !groupInfo,
            });

            const second = values.additionalMember;
            const secondRenderers = {};
            if (second) {
              const secondSetFieldValue = (name, value) =>
                setFieldValue(`additionalMember.${name}`, value);
              const sectionRenderers = makeSectionRenderers({
                values: second,
                errors: getIn(errors, "additionalMember") || {},
                touched: getIn(touched, "additionalMember") || {},
                setFieldValue: secondSetFieldValue,
                toggleInArray: (field, item) => {
                  const arr = second[field] || [];
                  secondSetFieldValue(
                    field,
                    arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item],
                  );
                },
                medicalClearanceRequired: parqRequiresMedicalClearance(second.parqAnswers),
                studentMember: false,
                showPaymentProof: false,
              });
              secondRenderers.personal = sectionRenderers.renderPersonal;
              secondRenderers.contact = sectionRenderers.renderContact;
              secondRenderers.membership = sectionRenderers.renderMembershipDetails;
              secondRenderers.health = sectionRenderers.renderHealth;
              secondRenderers.parq = sectionRenderers.renderParq;
              secondRenderers.agreement = sectionRenderers.renderAgreement;
              secondRenderers.finish = sectionRenderers.renderFinish;
            }
            const secondMemberActive = activeMember === "second" && Boolean(second);

            const familyIndex = activeMember.startsWith("family:")
              ? Number(activeMember.slice("family:".length))
              : null;
            const familyMember =
              familyIndex !== null ? (values.familyMembers || [])[familyIndex] : null;
            const familyMemberActive = Boolean(familyMember);
            const familyPath = `familyMembers.${familyIndex}`;
            const familyRenderers = {};
            if (familyMember) {
              const memberSetFieldValue = (name, value) =>
                setFieldValue(`${familyPath}.${name}`, value);
              const sectionRenderers = makeSectionRenderers({
                values: familyMember,
                errors: getIn(errors, familyPath) || {},
                touched: getIn(touched, familyPath) || {},
                setFieldValue: memberSetFieldValue,
                toggleInArray: (field, item) => {
                  const arr = familyMember[field] || [];
                  memberSetFieldValue(
                    field,
                    arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item],
                  );
                },
                medicalClearanceRequired: parqRequiresMedicalClearance(familyMember.parqAnswers),
                studentMember: false,
                nicOptional: isFamilyChild(values.membershipType, familyMember.age),
                showPaymentProof: false,
              });
              familyRenderers.personal = sectionRenderers.renderPersonal;
              familyRenderers.contact = sectionRenderers.renderContact;
              familyRenderers.membership = sectionRenderers.renderMembershipDetails;
              familyRenderers.health = sectionRenderers.renderHealth;
              familyRenderers.parq = sectionRenderers.renderParq;
              familyRenderers.agreement = sectionRenderers.renderAgreement;
              familyRenderers.finish = sectionRenderers.renderFinish;
            }

            // New family members start with the main member's shared contact details.
            const addFamilyMember = () => {
              const members = values.familyMembers || [];
              setFieldValue("familyMembers", [
                ...members,
                {
                  ...initialValues,
                  membershipType: values.membershipType,
                  nationality: values.nationality,
                  address: values.address,
                  mobileNumber: values.mobileNumber,
                  whatsappNumber: values.whatsappNumber,
                  email: values.email,
                  emergencyContactPerson: values.emergencyContactPerson,
                  emergencyContactRelationship: values.emergencyContactRelationship,
                  emergencyContactNumber: values.emergencyContactNumber,
                  membershipStartDate: values.membershipStartDate,
                  medicalConditions: { ...initialValues.medicalConditions },
                  parqAnswers: { ...initialValues.parqAnswers },
                  documentsAck: { ...initialValues.documentsAck },
                  trainingGoals: [],
                  preferredTrainingTimes: [],
                  paymentMethod: "",
                  membershipFee: "",
                  registrationFee: "",
                  totalAmountPaid: "",
                  paymentDate: "",
                  receiptNumber: "",
                },
              ]);
              setActiveMember(`family:${members.length}`);
              setActiveStep(1);
            };

            const removeFamilyMember = (index) => {
              setFieldValue(
                "familyMembers",
                (values.familyMembers || []).filter((_, i) => i !== index),
              );
              setActiveMember("primary");
            };

            const renderFamilyMembers = () => (
              <>
                <Divider sx={{ my: 2 }} />
                <SectionTitle subtitle="Add everyone joining with you. Each price is worked out from the date of birth.">
                  Family members
                </SectionTitle>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box
                    sx={{
                      py: 1.25,
                      px: 1.5,
                      borderRadius: 2,
                      bgcolor: "rgba(0,0,0,0.2)",
                      borderLeft: `3px solid ${HPC_COLORS.hpuOrange}`,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {values.fullName || "Main member"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Main member · {values.dateOfBirth || "date of birth not entered"}
                    </Typography>
                  </Box>
                  {(values.familyMembers || []).map((member, index) => (
                    <Box
                      key={index}
                      sx={{
                        py: 1.25,
                        px: 1.5,
                        borderRadius: 2,
                        bgcolor: "rgba(0,0,0,0.2)",
                        borderLeft: `3px solid ${HPC_COLORS.tebaBlue}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {member.fullName || `Family member ${index + 2}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {member.dateOfBirth || "date of birth not entered"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setActiveMember(`family:${index}`);
                            setActiveStep(1);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => removeFamilyMember(index)}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Button variant="outlined" fullWidth sx={{ mt: 2 }} onClick={addFamilyMember}>
                  Add family member
                </Button>
                <FamilyPriceTable
                  membershipType={values.membershipType}
                  members={familyQuoteMembers}
                  onQuote={handleFamilyQuote}
                />
              </>
            );

            const renderReview = () => {
              const isInitialCouple =
                !groupId &&
                membershipPackages.some(
                  (pkg) => pkg.id === values.membershipType && pkg.category === "couple",
                );
              const rows = [
                ["Name", values.fullName],
                ["Email", values.email],
                ["Mobile", values.mobileNumber],
                [
                  "Membership",
                  membershipPackages.find((pkg) => pkg.id === values.membershipType)?.label ||
                    labelFor(MEMBERSHIP_TYPE_OPTIONS, values.membershipType),
                ],

                ["Training goals", labelsForList(TRAINING_GOAL_OPTIONS, values.trainingGoals)],
                ...(isEdit || groupInfo
                  ? []
                  : [
                      ["Payment", labelFor(PAYMENT_METHOD_OPTIONS, values.paymentMethod)],
                      [
                        "Membership fee",
                        values.membershipFee ? formatLkr(values.membershipFee) : "",
                      ],
                      [
                        "Registration fee",
                        values.registrationFee ? formatLkr(values.registrationFee) : "",
                      ],
                      ...(values.promoCode && values.promoPricing && !values.promoPricing.no_extra_saving
                        ? [
                            [
                              values.promoPricing.promotion?.promotion_type === "ADMISSION_WAIVER"
                                ? "Normal price"
                                : "Original value",
                              formatLkr(values.promoPricing.promotion_base_value ?? values.promoPricing.original_value),
                            ],
                            [
                              "Promotion",
                              `${values.promoCode} (− ${formatLkr(values.promoPricing.discount_amount)})`,
                            ],
                            ["Normal price", formatLkr(values.promoPricing.base_total)],
                          ]
                        : []),
                      ...(values.promoCode && values.promoPricing?.no_extra_saving
                        ? [["Promotion", `${values.promoCode} (no extra saving for this package)`]]
                        : []),
                      [
                        groupPackage ? "Group total (paid by main member)" : "Total paid",
                        values.totalAmountPaid ? formatLkr(values.totalAmountPaid) : "",
                      ],
                    ]),
                ...(values.additionalMember && isInitialCouple
                  ? [["Additional member", values.additionalMember.fullName || "Details entered"]]
                  : []),
                ...(initialFamily
                  ? [["Family members", String(1 + (values.familyMembers || []).length)]]
                  : []),
                [
                  "Media consent",
                  values.mediaConsent === "allow" ? "Allow promotional use" : "Do not use photos/videos",
                ],
              ];
              return (
                <>
                  <SectionTitle subtitle="Confirm everything looks correct, then complete registration.">
                    Review & submit
                  </SectionTitle>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {rows.map(([k, v]) => (
                      <Box
                        key={k}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          py: 1.25,
                          px: 1.5,
                          borderRadius: 2,
                          bgcolor: "rgba(0,0,0,0.2)",
                          borderLeft: `3px solid ${HPC_COLORS.tebaBlue}`,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: HPC_COLORS.silver, flexShrink: 0 }}>
                          {k}
                        </Typography>
                        <Typography variant="body2" align="right" sx={{ fontWeight: 600 }}>
                          {v || "—"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {isInitialCouple && !values.additionalMember && (
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => {
                        setFieldValue("additionalMember", {
                          ...initialValues,
                          membershipType: values.membershipType,
                          medicalConditions: { ...initialValues.medicalConditions },
                          parqAnswers: { ...initialValues.parqAnswers },
                          documentsAck: { ...initialValues.documentsAck },
                          trainingGoals: [],
                          preferredTrainingTimes: [],
                          paymentMethod: "",
                          membershipFee: "",
                          registrationFee: "",
                          totalAmountPaid: "",
                          paymentDate: "",
                          receiptNumber: "",
                        });
                        setActiveMember("second");
                        setActiveStep(1);
                      }}
                    >
                      Add the second Couple member
                    </Button>
                  )}
                  {values.additionalMember && isInitialCouple && (
                    <>
                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() => {
                          setActiveMember("second");
                          setActiveStep(1);
                        }}
                      >
                        Edit second member details
                      </Button>
                      <Button
                        color="error"
                        variant="outlined"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() => {
                          setFieldValue("additionalMember", null);
                          setActiveMember("primary");
                        }}
                      >
                        Remove second member
                      </Button>
                    </>
                  )}
                  {initialFamily && renderFamilyMembers()}
                </>
              );
            };

            const stepRenderers = [
              renderPackageSelection,
              renderPersonal,
              renderContact,
              renderMembershipDetails,
              renderHealth,
              renderParq,
              renderAgreement,
              renderPayment,
              renderFinish,
              renderReview,
            ];

            const applyStepErrors = (stepErrors) => {
              setErrors(stepErrors);
              Object.keys(stepErrors).forEach((path) => {
                setFieldTouched(path, true, false);
              });
              toast.error(summarizeStepErrors(stepErrors));
            };

            const applySecondStepErrors = (stepErrors) => {
              let nested = {};
              Object.entries(stepErrors).forEach(([path, message]) => {
                nested = setIn(nested, path, message);
                setFieldTouched(`additionalMember.${path}`, true, false);
              });
              setErrors({ additionalMember: nested });
              toast.error(summarizeStepErrors(stepErrors));
            };

            const applyFamilyStepErrors = (stepErrors, index) => {
              let nested = {};
              Object.entries(stepErrors).forEach(([path, message]) => {
                nested = setIn(nested, path, message);
                setFieldTouched(`familyMembers.${index}.${path}`, true, false);
              });
              setErrors(setIn({}, `familyMembers.${index}`, nested));
              toast.error(summarizeStepErrors(stepErrors));
            };

            const clearMembershipSelection = () => {
              setFieldValue("membershipType", "");
              setFieldValue("membershipFee", "");
              setFieldValue("registrationFee", "");
              setFieldValue("totalAmountPaid", "");
            };

            const secondStepKey = STEPS[activeStep].key;
            const shellSteps = isEdit ? EDIT_STEPS : groupInfo ? JOIN_STEPS : STEPS;
            const goNext = async () => {
              if (familyMemberActive) {
                const stepErrors = await validateFamilyMemberStep(
                  familyMember,
                  values.membershipType,
                  secondStepKey,
                );
                if (Object.keys(stepErrors).length > 0) {
                  applyFamilyStepErrors(stepErrors, familyIndex);
                  return;
                }
                if (secondStepKey === "finish") {
                  setActiveMember("primary");
                  setActiveStep(STEPS.length - 1);
                } else {
                  setActiveStep((s) => (STEPS[s + 1].key === "payment" ? s + 2 : s + 1));
                }
                return;
              }
              if (secondMemberActive) {
                const stepErrors = await validateSubmemberStep(
                  second,
                  values.membershipType,
                  secondStepKey,
                );
                if (Object.keys(stepErrors).length > 0) {
                  applySecondStepErrors(stepErrors);
                  return;
                }
                if (secondStepKey === "finish") {
                  setActiveMember("primary");
                  setActiveStep(STEPS.length - 1);
                } else {
                  setActiveStep((s) => (STEPS[s + 1].key === "payment" ? s + 2 : s + 1));
                }
                return;
              }
              const stepErrors = await validateStep(values, activeStep);
              if (Object.keys(stepErrors).length > 0) {
                applyStepErrors(stepErrors);
                return;
              }
              if (activeStep === 7 && values.fullName && !values.declarationName) {
                setFieldValue("declarationName", values.fullName);
              }
              if (
                !isEdit &&
                activeStep === 1 &&
                values.membershipType &&
                memberAge !== "" &&
                !isPackageEligible(values.membershipType, memberAge)
              ) {
                clearMembershipSelection();
                setActiveStep(0);
                return;
              }
              setActiveStep((s) => {
                const next = Math.min(s + 1, STEPS.length - 1);
                return skipPaymentStep && STEPS[next].key === "payment" ? next + 1 : next;
              });
            };

            const goBack = () => {
              if (familyMemberActive) {
                if (secondStepKey === "personal") {
                  setActiveMember("primary");
                  setActiveStep(STEPS.length - 1);
                } else {
                  setActiveStep((s) => (STEPS[s - 1].key === "payment" ? s - 2 : s - 1));
                }
                return;
              }
              if (secondMemberActive) {
                if (secondStepKey === "personal") {
                  setActiveMember("primary");
                  setActiveStep(STEPS.length - 1);
                } else {
                  setActiveStep((s) => (STEPS[s - 1].key === "payment" ? s - 2 : s - 1));
                }
                return;
              }
              setActiveStep((s) => {
                const prev = Math.max(s - 1, isEdit ? 1 : 0);
                return skipPaymentStep && STEPS[prev].key === "payment" ? prev - 1 : prev;
              });
            };

            const onPrimary = async () => {
              if (secondMemberActive || familyMemberActive || activeStep < STEPS.length - 1) {
                goNext();
                return;
              }
              for (let i = isEdit ? 1 : 0; i < STEPS.length - 1; i += 1) {
                if (skipPaymentStep && STEPS[i].key === "payment") continue;
                const stepErrors = await validateStep(values, i);
                if (Object.keys(stepErrors).length > 0) {
                  setActiveStep(i);
                  applyStepErrors(stepErrors);
                  return;
                }
              }
              const isInitialCouple =
                !groupId &&
                membershipPackages.some(
                  (pkg) => pkg.id === values.membershipType && pkg.category === "couple",
                );
              if (isInitialCouple && values.additionalMember) {
                for (let i = 1; i < STEPS.length - 1; i += 1) {
                  if (STEPS[i].key === "payment") continue;
                  const stepErrors = await validateSubmemberStep(
                    values.additionalMember,
                    values.membershipType,
                    STEPS[i].key,
                  );
                  if (Object.keys(stepErrors).length > 0) {
                    setActiveMember("second");
                    setActiveStep(i);
                    applySecondStepErrors(stepErrors);
                    return;
                  }
                }
              }
              if (isInitialCouple && values.additionalMember) {
                const memberErrors = await validateSubmemberValues(
                  values.additionalMember,
                  values.membershipType,
                );
                if (Object.keys(memberErrors).length > 0) {
                  let nestedErrors = {};
                  Object.entries(memberErrors).forEach(([path, message]) => {
                    const fullPath = `additionalMember.${path}`;
                    nestedErrors = setIn(nestedErrors, fullPath, message);
                    setFieldTouched(fullPath, true, false);
                  });
                  setErrors(nestedErrors);
                  toast.error(
                    "Please complete and correct the additional Couple member's details.",
                  );
                  return;
                }
              }
              if (initialFamily) {
                const members = values.familyMembers || [];
                for (let index = 0; index < members.length; index += 1) {
                  const memberErrors = await validateFamilyMemberValues(
                    members[index],
                    values.membershipType,
                  );
                  if (Object.keys(memberErrors).length > 0) {
                    for (let i = 1; i < STEPS.length - 1; i += 1) {
                      if (STEPS[i].key === "payment") continue;
                      const stepErrors = await validateFamilyMemberStep(
                        members[index],
                        values.membershipType,
                        STEPS[i].key,
                      );
                      if (Object.keys(stepErrors).length > 0) {
                        setActiveMember(`family:${index}`);
                        setActiveStep(i);
                        applyFamilyStepErrors(stepErrors, index);
                        return;
                      }
                    }
                  }
                }
              }
              formikSubmit();
            };

            return (
              <HpcShell
                activeStep={shellSteps.findIndex((s) => s.key === STEPS[activeStep].key)}
                steps={shellSteps}
                wizardTitle={isEdit ? `Edit member ${editMembershipId}` : undefined}
                footer={stepFooter(
                  goBack,
                  onPrimary,
                  submitting,
                  !secondMemberActive && !familyMemberActive && activeStep === STEPS.length - 1,
                )}
              >
                {secondMemberActive && (
                  <Typography variant="caption" color="text.secondary">
                    Second member
                  </Typography>
                )}
                {familyMemberActive && (
                  <Typography variant="caption" color="text.secondary">
                    Family member {familyIndex + 2}
                  </Typography>
                )}
                {familyMemberActive
                  ? familyRenderers[secondStepKey]()
                  : secondMemberActive
                    ? secondRenderers[secondStepKey]()
                    : stepRenderers[activeStep]()}
              </HpcShell>
            );
          }}
        </Formik>
      <ToastContainer position="top-center" theme="dark" />
    </ThemeProvider>
  );
};

export default GymRegistration;
